import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { KpiCard } from "../../../Component/RetailerMainComponents/KpiCard";
import { SectionCard } from "../../../Component/RetailerMainComponents/SectionCard";
import RefreshButton from "../../../Component/Common/RefreshButton";

import {
  UsersIcon,
  ShieldCheck,
  FileText,
  Wallet,
  UserPlus,
} from "lucide-react"; 

import { BASE_URL } from "../../../config/api";
import SimpleTable from "../../../Component/Common/SimpleTable";
import StatusButton from "../../../Component/Common/StatusButton";
import ViewButton from "../../../Component/Common/ViewButton";
import SearchInput from "../../../Component/Common/SearchInput";
import Badge from "../../../Component/Common/Badge";
import RegularLoader from "../../../Component/Loader/RegularLoader";
import { UserCard } from "./UserCard";

function EmptyState({ text = "No data available" }) {
  return (
    <div className="py-10 text-center text-sm text-slate-500">
      {text}
    </div>
  );
}

export default function UsersView() {
  const navigate = useNavigate();

  /* ---------------- SESSION ---------------- */
  const authUser = JSON.parse(localStorage.getItem("authUserData"));
  const retailerId = authUser?.id;

  /* ---------------- STATE ---------------- */
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [actionLoadingId, setActionLoadingId] = useState(null);
  const [search, setSearch] = useState("");

  /* ---------------- FETCH USERS ---------------- */
  const fetchUsers = async (page = 1, searchValue = search) => {
  try {
    setLoading(true);

    const res = await axios.get(`${BASE_URL}common/get-users`, {
      params: {
        retailer_id: retailerId,
        role: 6,
        page,
        search_value: searchValue || undefined, // 🔍 SEARCH
      },
    });

    setUsers(res.data.data || []);
    setCurrentPage(res.data.pagination.current_page);
    setTotalPages(res.data.pagination.last_page);
  } catch (err) {
    console.error(err);
  } finally {
    setLoading(false);
  }
};

  useEffect(() => {
    if (retailerId) fetchUsers(currentPage);
  }, [retailerId, currentPage]);

  useEffect(() => {
  if (!retailerId) return;

  const t = setTimeout(() => {
    setCurrentPage(1);
    fetchUsers(1, search);
  }, 400); // ⏱ debounce

  return () => clearTimeout(t);
}, [search, retailerId]);


  /* ---------------- KPI STATS ---------------- */
  const stats = useMemo(() => {
    let active = 0;
    let inactive = 0;

    users.forEach((u) => {
      u.status === 1 ? active++ : inactive++;
    });

    return {
      total: users.length,
      active,
      inactive,
      lastLogin: users[0]?.updated_at
        ? new Date(users[0].updated_at).toLocaleString()
        : "-",
    };
  }, [users]);

  /* ---------------- TABLE HEADERS ---------------- */
  const headers = [
    "User",
    "Mobile",
    "Email",
    "Role",
    "Status",
    "Created At",
  ];

  /* ---------------- TABLE ROWS ---------------- */
  const rows = users.map((u) => ({
    raw: u,
    cells: [
      {
        title: u.business_name,
        sub: u.company_code,
      },
      u.contact_phone || "-",
      u.contact_email || "-",
      u.role === 6 ? "Promoter" : "-",
      <Badge
        label={u.status === 1 ? "Active" : "Inactive"}
        color={u.status === 1 ? "blue" : "red"}
      />,
      new Date(u.created_at).toLocaleDateString(),
    ],
  }));

   const handleStatusToggle = async (user) => {
  try {
    setActionLoadingId(user.id);

    await axios.post(`${BASE_URL}common/update-user-status`, {
      user_id: user.id,
      status: user.status === 1 ? 0 : 1,
    });

    fetchUsers(currentPage);
  } catch (err) {
    console.error("Failed to update status", err);
  } finally {
    setActionLoadingId(null);
  }
};

  /* ---------------- ACTIONS ---------------- */
  const renderActions = (user) => {
  const isActive = user.status === 1;
  const isLoading = actionLoadingId === user.id;

  return (
    <div className="flex justify-center gap-2">
      {/* 👁 VIEW BUTTON */}
      <ViewButton
        size="sm"
        onClick={() =>
          navigate(`/user/${user.id}`, {
            state: { entityType: "retailer_user", data: user },
          })
        }
      />

      {/* 🔄 STATUS BUTTON */}
      <StatusButton
        label={isLoading ? "Updating..." : isActive ? "Deactivate" : "Activate"}
        onClick={() => handleStatusToggle(user)}
        disabled={isLoading}
        className={
          isActive ? "text-red-600" : "text-green-600"
        }
      />
    </div>
  );
};

const [isMobile, setIsMobile] = useState(window.innerWidth < 640);

useEffect(() => {
  const handleResize = () => setIsMobile(window.innerWidth < 640);
  window.addEventListener("resize", handleResize);
  return () => window.removeEventListener("resize", handleResize);
}, []);

 const isRetailerMobile =
    (Number(authUser?.role) === 5 || Number(authUser?.role) === 6) && isMobile;

  return (
    <div className="space-y-4">
      {/* ---------- KPI CARDS ---------- */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard
          label="Total Users"
          value={stats.total}
          subLabel="Retailer users"
          icon={UsersIcon}
          isRetailerMobile={isRetailerMobile}
        />
        <KpiCard
          label="Active Users"
          value={stats.active}
          subLabel="Can login"
          icon={ShieldCheck}
          isRetailerMobile={isRetailerMobile}
        />
        <KpiCard
          label="Inactive Users"
          value={stats.inactive}
          subLabel="Disabled access"
          icon={FileText}
          isRetailerMobile={isRetailerMobile}
        />
        <KpiCard
          label="Most Recent Activity"
          value={stats.lastLogin}
          subLabel="Last update"
          icon={Wallet}
          isRetailerMobile={isRetailerMobile}
        />
      </div>

      {/* ---------- USERS TABLE ---------- */}
      <SectionCard
        title="Users & Access"
        isRetailerMobile={isRetailerMobile}
      >
        <div className="flex justify-end items-center gap-1 mb-3">
            <SearchInput
              value={search}
              onChange={setSearch}
              onClear={() => setSearch("")}
              placeholder="Search user / mobile / email"
              size="sm"
              isRetailerMobile={isRetailerMobile}
            />

            <button
              onClick={() =>
                navigate("/add-entity", {
                    state: { 
                      leadType: 6,   // this becomes roleFromState
                      from: "retailer_user"
                    },
                  })

              }
              className="text-[11px] px-2.5 py-1.5 rounded-lg
              border border-blue-200 text-blue-700 bg-blue-50 hover:bg-blue-100
              inline-flex items-center gap-1"
            >
              <UserPlus className="w-3 h-3 " />
              <span className="hidden md:block">Add User</span>
            </button>

            <RefreshButton onClick={() => fetchUsers(currentPage)} />
          </div>
        {loading ? (
          <div className="py-10 text-center text-sm text-slate-500">
            <RegularLoader/>
          </div>
        ) : users.length === 0 ? (
          <EmptyState text="No data available" />
        ) : (
          <>
            {/* 📱 MOBILE CARD VIEW */}
            <div className="grid gap-3 md:hidden">
              {users.map((user) => (
                <UserCard
                  key={user.id}
                  user={user}
                  loading={actionLoadingId === user.id}
                  onView={(u) =>
                    navigate(`/user/${u.id}`, {
                      state: { entityType: "retailer_user", data: u },
                    })
                  }
                  onToggle={handleStatusToggle}
                  isRetailerMobile={isRetailerMobile}
                />
              ))}
            </div>

            {/* 🖥 DESKTOP TABLE VIEW */}
            <div className="hidden md:block">
              <SimpleTable
                headers={headers}
                rows={rows}
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
                renderActions={renderActions}
                align="left"
              />
            </div>
          </>
        )}

      </SectionCard>
    </div>
  );
}



// import React, { useMemo, useState } from "react";
// import { KpiCard } from "../../../Component/RetailerMainComponents/KpiCard";
// import { FileText, ShieldCheck, UserPlus, UsersIcon, Wallet } from "lucide-react";
// import { SectionCard } from "../../../Component/RetailerMainComponents/SectionCard";
// import { Badge } from "../../../Component/RetailerMainComponents/Badge";
// import Pagination from "../../../Component/Common/Pagination";
// import RefreshButton from "../../../Component/Common/RefreshButton";
// import { useNavigate } from "react-router-dom";


// // ------------------------
// // LOCAL DATA
// // ------------------------
// const mockUsers = [
//   {
//     id: 1,
//     name: "Deepak Sharma",
//     mobile: "9876543210",
//     email: "deepak@example.com",
//     role: "Owner",
//     status: "Active",
//     lastLogin: "2025-11-18 10:22 AM",
//   },
//   {
//     id: 2,
//     name: "Rohan Patil",
//     mobile: "9822334455",
//     email: "rohan@example.com",
//     role: "Staff",
//     status: "Inactive",
//     lastLogin: "2025-10-05 03:40 PM",
//   },
//   {
//     id: 3,
//     name: "Sneha Kulkarni",
//     mobile: "9988776655",
//     email: "sneha@example.com",
//     role: "Cashier",
//     status: "Blocked",
//     lastLogin: "-",
//   },
// ];

// export default function UsersView() {
//   const users = mockUsers;
//   const navigate = useNavigate();

//   // ⭐ PAGINATION STATES
//   const [currentPage, setCurrentPage] = useState(1);
//   const itemsPerPage = 2; // CHANGE IF NEEDED

//   const totalPages = Math.ceil(users.length / itemsPerPage);

//   const paginatedUsers = useMemo(() => {
//     const start = (currentPage - 1) * itemsPerPage;
//     return users.slice(start, start + itemsPerPage);
//   }, [users, currentPage]);

//   // ⭐ STATS
//   const stats = useMemo(() => {
//     let active = 0;
//     let inactive = 0;
//     let blocked = 0;

//     users.forEach((u) => {
//       if (u.status === "Active") active++;
//       else if (u.status === "Blocked") blocked++;
//       else inactive++;
//     });

//     const lastLogin = users[0]?.lastLogin || "-";

//     return { total: users.length, active, inactive, blocked, lastLogin };
//   }, [users]);

//   return (
//     <div className="space-y-4">
//       {/* ---------- KPI CARDS ---------- */}
//       <div className="grid grid-cols-2 md:grid-cols-4 sm:grid-cols-4 gap-3">
//         <KpiCard label="Total Users" value={stats.total} subLabel="All retailer logins" icon={UsersIcon} />
//         <KpiCard label="Active Users" value={stats.active} subLabel="Can login & transact" icon={ShieldCheck} />
//         <KpiCard label="Inactive / Blocked" value={stats.inactive + stats.blocked} subLabel={`Inactive: ${stats.inactive}, Blocked: ${stats.blocked}`} icon={FileText} />
//         <KpiCard label="Most Recent Login" value={stats.lastLogin} subLabel="Last user activity" icon={Wallet} />
//       </div>

//       {/* ---------- USERS TABLE ---------- */}
//       <SectionCard
//         title="Users & Access"
//         right={
//           <div className="flex justify-end gap-2">
//           <button
//             onClick={() =>
//               navigate("/add-entity/retailer-user", {
//                 state: {
//                   role: 6,           // ✅ retailer user role
//                   from: "retailer",  // optional
//                 },
//               })
//             }
//             className="text-[11px] px-2.5 py-1.5 rounded-lg border border-blue-200 text-blue-700 bg-blue-50 hover:bg-blue-100 inline-flex items-center gap-1"
//           >
//             <UserPlus className="w-3 h-3" />
//             <span>Add User</span>
//           </button>


//           <RefreshButton/>
//           </div>
//         }
//       >
//         <div className="overflow-x-auto">
//           <table className="min-w-full text-xs">
//             <thead>
//               <tr className="border-b border-slate-100 text-slate-500">
//                 <th className="py-2 text-left">User</th>
//                 <th className="py-2 text-left">Mobile</th>
//                 <th className="py-2 text-left">Email</th>
//                 <th className="py-2 text-left">Role</th>
//                 <th className="py-2 text-left">Status</th>
//                 <th className="py-2 text-left">Last Login</th>
//                 <th className="py-2 text-right">Action</th>
//               </tr>
//             </thead>

//             <tbody>
//               {paginatedUsers.map((u) => {
//                 const statusColor =
//                   u.status === "Active" ? "green" : u.status === "Blocked" ? "red" : "gray";

//                 const actionLabel = u.status === "Active" ? "Deactivate" : "Activate";

//                 return (
//                   <tr key={u.id} className="border-b border-slate-50 bg-white hover:bg-blue-50/60">
//                     <td className="py-1.5 text-xs text-slate-700">
//                       <div className="font-medium">{u.name}</div>
//                       <div className="text-[10px] text-slate-500">ID: {u.id}</div>
//                     </td>
//                     <td className="py-1.5 text-xs">{u.mobile}</td>
//                     <td className="py-1.5 text-xs">{u.email}</td>
//                     <td className="py-1.5 text-xs">{u.role}</td>
//                     <td className="py-1.5 text-xs">
//                       <Badge color={statusColor}>{u.status}</Badge>
//                     </td>
//                     <td className="py-1.5 text-xs">{u.lastLogin}</td>
//                     <td className="py-1.5 text-xs text-right">
//                       <button
//                         type="button"
//                         className="inline-flex items-center gap-1 text-[11px] px-2.5 py-1.5 rounded-lg border border-slate-200 text-slate-700 bg-slate-50 hover:bg-slate-100"
//                       >
//                         <ShieldCheck className="w-3 h-3" />
//                         <span>{actionLabel}</span>
//                       </button>
//                     </td>
//                   </tr>
//                 );
//               })}

//               {users.length === 0 && (
//                 <tr>
//                   <td colSpan={7} className="py-4 text-center text-xs text-slate-500">
//                     No users configured yet. Use "Add User" to invite team members.
//                   </td>
//                 </tr>
//               )}
//             </tbody>
//           </table>
//         </div>

//         {/* ⭐ PAGINATION SECTION ⭐ */}
//         {totalPages > 1 && (
//           <div className="mt-3 flex justify-end">
//             <Pagination
//               currentPage={currentPage}
//               totalPages={totalPages}
//               onPageChange={setCurrentPage}
//             />
//           </div>
//         )}
//       </SectionCard>
//     </div>
//   );
// }


// import { useMemo } from "react";
// import { KpiCard } from "../../../Component/RetailerMainComponents/KpiCard";
// import { FileText, ShieldCheck, UserPlus, UsersIcon, Wallet } from "lucide-react";
// import { SectionCard } from "../../../Component/RetailerMainComponents/SectionCard";
// import { Badge } from "../../../Component/RetailerMainComponents/Badge";

// // ------------------------
// // LOCAL DATA (NO IMPORT)
// // ------------------------
// const mockUsers = [
//   {
//     id: 1,
//     name: "Deepak Sharma",
//     mobile: "9876543210",
//     email: "deepak@example.com",
//     role: "Owner",
//     status: "Active",
//     lastLogin: "2025-11-18 10:22 AM",
//   },
//   {
//     id: 2,
//     name: "Rohan Patil",
//     mobile: "9822334455",
//     email: "rohan@example.com",
//     role: "Staff",
//     status: "Inactive",
//     lastLogin: "2025-10-05 03:40 PM",
//   },
//   {
//     id: 3,
//     name: "Sneha Kulkarni",
//     mobile: "9988776655",
//     email: "sneha@example.com",
//     role: "Cashier",
//     status: "Blocked",
//     lastLogin: "-",
//   },
// ];

// export default function UsersView() {
//   const users = mockUsers; // <--- DIRECT DATA (NO PROPS)

//   const stats = useMemo(() => {
//     let active = 0;
//     let inactive = 0;
//     let blocked = 0;

//     users.forEach((u) => {
//       if (u.status === "Active") active++;
//       else if (u.status === "Blocked") blocked++;
//       else inactive++;
//     });

//     const lastLogin = users[0]?.lastLogin || "-";

//     return { total: users.length, active, inactive, blocked, lastLogin };
//   }, [users]);

//   return (
//     <div className="space-y-4">
//       <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
//         <KpiCard
//           label="Total Users"
//           value={stats.total}
//           subLabel="All retailer logins"
//           icon={UsersIcon}
//         />
//         <KpiCard
//           label="Active Users"
//           value={stats.active}
//           subLabel="Can login & transact"
//           icon={ShieldCheck}
//         />
//         <KpiCard
//           label="Inactive / Blocked"
//           value={stats.inactive + stats.blocked}
//           subLabel={`Inactive: ${stats.inactive}, Blocked: ${stats.blocked}`}
//           icon={FileText}
//         />
//         <KpiCard
//           label="Most Recent Login"
//           value={stats.lastLogin}
//           subLabel="Last user activity"
//           icon={Wallet}
//         />
//       </div>

//       <SectionCard
//         title="Users & Access"
//         right={
//           <button className="text-[11px] px-2.5 py-1.5 rounded-lg border border-blue-200 text-blue-700 bg-blue-50 hover:bg-blue-100 inline-flex items-center gap-1">
//             <UserPlus className="w-3 h-3" />
//             <span>Add User</span>
//           </button>
//         }
//       >
//         <div className="overflow-x-auto">
//           <table className="min-w-full text-xs">
//             <thead>
//               <tr className="border-b border-slate-100 text-slate-500">
//                 <th className="py-2 text-left">User</th>
//                 <th className="py-2 text-left">Mobile</th>
//                 <th className="py-2 text-left">Email</th>
//                 <th className="py-2 text-left">Role</th>
//                 <th className="py-2 text-left">Status</th>
//                 <th className="py-2 text-left">Last Login</th>
//                 <th className="py-2 text-right">Action</th>
//               </tr>
//             </thead>
//             <tbody>
//               {users.map((u) => {
//                 const isActive = u.status === "Active";
//                 const statusColor =
//                   isActive ? "green" : u.status === "Blocked" ? "red" : "gray";

//                 const actionLabel = isActive ? "Deactivate" : "Activate";

//                 return (
//                   <tr
//                     key={u.id}
//                     className="border-b border-slate-50 bg-white hover:bg-blue-50/60"
//                   >
//                     <td className="py-1.5 text-xs text-slate-700">
//                       <div className="font-medium">{u.name}</div>
//                       <div className="text-[10px] text-slate-500">ID: {u.id}</div>
//                     </td>
//                     <td className="py-1.5 text-xs text-slate-700">{u.mobile}</td>
//                     <td className="py-1.5 text-xs text-slate-700">{u.email}</td>
//                     <td className="py-1.5 text-xs text-slate-700">{u.role}</td>
//                     <td className="py-1.5 text-xs">
//                       <Badge color={statusColor}>{u.status}</Badge>
//                     </td>
//                     <td className="py-1.5 text-xs text-slate-700">{u.lastLogin}</td>
//                     <td className="py-1.5 text-xs text-right">
//                       <button
//                         type="button"
//                         className="inline-flex items-center gap-1 text-[11px] px-2.5 py-1.5 rounded-lg border border-slate-200 text-slate-700 bg-slate-50 hover:bg-slate-100"
//                       >
//                         <ShieldCheck className="w-3 h-3" />
//                         <span>{actionLabel}</span>
//                       </button>
//                     </td>
//                   </tr>
//                 );
//               })}

//               {users.length === 0 && (
//                 <tr>
//                   <td
//                     colSpan={7}
//                     className="py-4 text-center text-xs text-slate-500"
//                   >
//                     No users configured yet. Use "Add User" to invite team
//                     members.
//                   </td>
//                 </tr>
//               )}
//             </tbody>
//           </table>
//         </div>
//       </SectionCard>
//     </div>
//   );
// }
